import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import config from "../../Config";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import moment from "moment";

/*
 * Qualifications held by one employee - one row per degree, with the scanned certificate.
 *
 * Rendered inside the Edit Employee page but self-contained: it loads and saves its own
 * data and its modal carries its own <Form>, so nothing here nests inside the employee
 * form. Percentage is shown live as marks are typed, but the stored figure is computed
 * again on the server so the two cannot drift apart.
 */

const LEVELS = [
  { value: "Matric", label: "Matriculation / SSC" },
  { value: "Intermediate", label: "Intermediate / HSSC" },
  { value: "Diploma", label: "Diploma" },
  { value: "Bachelor", label: "Bachelor (2-year)" },
  { value: "BachelorHons", label: "Bachelor (Hons / 4-year)" },
  { value: "Master", label: "Master" },
  { value: "MPhil", label: "M.Phil / MS" },
  { value: "PhD", label: "PhD" },
  { value: "PostDoc", label: "Post-Doctorate" },
  { value: "Certificate", label: "Certificate / Other" },
];

const levelLabel = (v) => (LEVELS.find((l) => l.value === v) || { label: v }).label;

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

function StaffQualifications({ staffId, staffName }) {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [highest, setHighest] = useState(null);

  const blank = {
    ID: 0,
    Degree_Level: "",
    Degree_Title: "",
    Major_Subject: "",
    Institution: "",
    Study_Mode: "Regular",
    Passing_Year: "",
    Total_Marks: "",
    Obtained_Marks: "",
    CGPA: "",
    Grade: "",
    Remarks: "",
    Attachment_Original_Name: "",
    Attachment_Url: null,
  };
  const [showForm, setShowForm] = useState(false);
  const [entry, setEntry] = useState(blank);
  const [file, setFile] = useState(null); // { name, base64 }
  const [validated, setValidated] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showVerify, setShowVerify] = useState(false);
  const [verifyRow, setVerifyRow] = useState(null);
  const [verifyRemarks, setVerifyRemarks] = useState("");

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      Staff_ID: parseInt(staffId, 10),
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType, staffId]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    post("StaffQualification/GetQualifications", auth({}))
      .then((res) => {
        if (res.data.status_code === 1) {
          setRows(res.data.ListofRecords || []);
          setHighest(res.data.Highest || null);
        } else {
          setRows([]);
          setHighest(null);
        }
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [post, auth]);

  useEffect(() => {
    if (staffId) load();
  }, [staffId, load]);

  /* ---- helpers ---- */

  const pct = (obt, tot) => {
    const o = parseFloat(obt);
    const t = parseFloat(tot);
    if (!(t > 0) || !(o >= 0)) return null;
    return Math.round((o / t) * 10000) / 100;
  };

  const livePercentage = pct(entry.Obtained_Marks, entry.Total_Marks);
  const marksExceed =
    entry.Total_Marks !== "" &&
    entry.Obtained_Marks !== "" &&
    parseFloat(entry.Obtained_Marks) > parseFloat(entry.Total_Marks);

  const fmt = (n, d = 2) =>
    n === null || n === undefined || n === "" ? "—" : Number(n).toFixed(d).replace(/\.?0+$/, "");

  const sizeLabel = (bytes) =>
    !bytes ? "" : bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

  /* ---- add / edit ---- */

  const openAdd = () => {
    setEntry(blank);
    setFile(null);
    setValidated(false);
    setShowForm(true);
  };

  const openEdit = (q) => {
    setEntry({
      ID: q.ID,
      Degree_Level: q.Degree_Level || "",
      Degree_Title: q.Degree_Title || "",
      Major_Subject: q.Major_Subject || "",
      Institution: q.Institution || "",
      Study_Mode: q.Study_Mode || "Regular",
      Passing_Year: q.Passing_Year ?? "",
      Total_Marks: q.Total_Marks ?? "",
      Obtained_Marks: q.Obtained_Marks ?? "",
      CGPA: q.CGPA ?? "",
      Grade: q.Grade || "",
      Remarks: q.Remarks || "",
      Attachment_Original_Name: q.Attachment_Original_Name || "",
      Attachment_Url: q.Attachment_Url || null,
    });
    setFile(null);
    setValidated(false);
    setShowForm(true);
  };

  const set = (field, value) => setEntry((prev) => ({ ...prev, [field]: value }));

  const onFileChosen = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      toast.error(`"${f.name}" is ${sizeLabel(f.size)}; the limit is 5 MB.`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      e.target.value = "";
      setFile(null);
      return;
    }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      toast.error("Only PDF, JPG and PNG files are accepted.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      e.target.value = "";
      setFile(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, base64: reader.result, size: f.size });
    reader.readAsDataURL(f);
  };

  const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

  const save = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    if (form.checkValidity() === false || marksExceed) {
      setValidated(true);
      return;
    }

    setSaving(true);
    post(
      "StaffQualification/SaveQualification",
      auth({
        ID: entry.ID,
        Degree_Level: entry.Degree_Level,
        Degree_Title: entry.Degree_Title,
        Major_Subject: entry.Major_Subject,
        Institution: entry.Institution,
        Study_Mode: entry.Study_Mode,
        Passing_Year: entry.Passing_Year === "" ? null : parseInt(entry.Passing_Year, 10),
        Total_Marks: num(entry.Total_Marks),
        Obtained_Marks: num(entry.Obtained_Marks),
        CGPA: num(entry.CGPA),
        Grade: entry.Grade,
        Remarks: entry.Remarks,
        Attachment_Base64: file ? file.base64 : null,
        Attachment_Name: file ? file.name : null,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowForm(false);
          load();
        } else {
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
          });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setSaving(false));
  };

  const removeAttachment = () => {
    if (!window.confirm("Remove the scanned copy from this qualification?")) return;
    post("StaffQualification/RemoveAttachment", auth({ ID: entry.ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setEntry((prev) => ({ ...prev, Attachment_Original_Name: "", Attachment_Url: null }));
          load();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  const remove = (q) => {
    if (!window.confirm(`Delete ${q.Degree_Title}${q.Major_Subject ? ` (${q.Major_Subject})` : ""} from ${staffName || "this employee"}'s record?`)) {
      return;
    }
    post("StaffQualification/DeleteQualification", auth({ ID: q.ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          load();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  /* ---- verification ---- */

  const openVerify = (q) => {
    setVerifyRow(q);
    setVerifyRemarks(q.Verification_Remarks || "");
    setShowVerify(true);
  };

  const setVerification = (verified) => {
    post(
      "StaffQualification/VerifyQualification",
      auth({ ID: verifyRow.ID, Is_Verified: verified, Verification_Remarks: verifyRemarks })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowVerify(false);
          load();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  /* ---- render ---- */

  const renderRows = () => {
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan="12" className="text-center text-muted py-4">
            No qualifications recorded yet
          </td>
        </tr>
      );
    }

    return rows.map((q, i) => (
      <tr key={q.ID}>
        <td>{i + 1}</td>
        <td>
          <span className="text-nowrap">{levelLabel(q.Degree_Level)}</span>
        </td>
        <td className="fw-bold">{q.Degree_Title}</td>
        <td>{q.Major_Subject || "—"}</td>
        <td>{q.Institution}</td>
        <td>{q.Study_Mode}</td>
        <td className="text-end">{q.Passing_Year || "—"}</td>
        <td className="text-end text-nowrap">
          {q.Obtained_Marks !== null && q.Total_Marks !== null
            ? `${fmt(q.Obtained_Marks)} / ${fmt(q.Total_Marks)}`
            : "—"}
        </td>
        <td className="text-end">{q.Percentage !== null ? `${fmt(q.Percentage)}%` : "—"}</td>
        <td className="text-end">
          {q.CGPA !== null ? fmt(q.CGPA) : "—"}
          {q.Grade ? <span className="text-muted small"> / {q.Grade}</span> : null}
        </td>
        <td>
          {q.Is_Verified ? (
            <span
              className="badge bg-success text-white"
              title={`Verified by ${q.Verified_By || "—"} on ${
                q.Verified_Date ? moment(q.Verified_Date).format("DD-MMM-YYYY") : "—"
              }`}
            >
              verified
            </span>
          ) : (
            <span className="badge bg-warning text-dark">unverified</span>
          )}
          {q.Attachment_Url ? (
            <a
              className="ms-2"
              href={config.root + q.Attachment_Url}
              target="_blank"
              rel="noreferrer"
              title={`${q.Attachment_Original_Name} (${sizeLabel(q.Attachment_Size)})`}
            >
              <i className="ri-attachment-2"></i>
            </a>
          ) : (
            <i className="ri-attachment-2 ms-2 text-muted" title="No scanned copy on file"></i>
          )}
        </td>
        <td>
          <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
            <li>
              <button type="button" className="green" title="Edit" onClick={() => openEdit(q)}>
                <i className="ri-edit-line"></i>
              </button>
            </li>
            <li>
              <button type="button" className="blue" title="Verification" onClick={() => openVerify(q)}>
                <i className="ri-shield-check-line"></i>
              </button>
            </li>
            <li>
              <button type="button" className="red" title="Delete" onClick={() => remove(q)}>
                <i className="ri-delete-bin-line"></i>
              </button>
            </li>
          </ul>
        </td>
      </tr>
    ));
  };

  return (
    <>
      {/* The legend floats 22px above the box; without this margin the card clips it. */}
      <div className="form-wrap mt-4">
        <div className="form-legend" style={{ left: "64px" }}>
          Qualifications
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="small">
            {highest ? (
              <>
                <span className="text-muted">Highest:</span>{" "}
                <b>{highest.Degree_Title}</b>
                {highest.Major_Subject ? ` (${highest.Major_Subject})` : ""}
                {highest.Passing_Year ? `, ${highest.Passing_Year}` : ""}
                {" · "}
                <span className="text-muted">
                  {rows.length} {rows.length === 1 ? "degree" : "degrees"} on file,{" "}
                  {rows.filter((r) => r.Is_Verified).length} verified,{" "}
                  {rows.filter((r) => r.Attachment_Url).length} with scanned copy
                </span>
              </>
            ) : (
              <span className="text-muted">
                Record each degree separately — the highest is worked out from the level.
              </span>
            )}
          </div>
          <button type="button" className="button button-primary button-small" onClick={openAdd}>
            <i className="ri-add-line me-1"></i>Add Qualification
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-theme">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Level</th>
                <th>Degree</th>
                <th>Major</th>
                <th>Institution</th>
                <th>Mode</th>
                <th className="text-end">Year</th>
                <th className="text-end">Marks</th>
                <th className="text-end">%</th>
                <th className="text-end">CGPA / Grade</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center py-3">
                    <Spinner animation="border" size="sm" role="status" />
                  </td>
                </tr>
              ) : (
                renderRows()
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* add / edit */}
      <Modal className="modal-theme" show={showForm} onHide={() => setShowForm(false)} size="lg" centered>
        <Form noValidate validated={validated} onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px" }}>
              {entry.ID ? "Edit qualification" : "Add qualification"}
              {staffName ? <span className="text-muted fw-normal"> — {staffName}</span> : null}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">
                  Degree level <span className="text-danger">*</span>
                </label>
                <Form.Select required value={entry.Degree_Level} onChange={(e) => set("Degree_Level", e.target.value)}>
                  <option value="">-- select --</option>
                  {LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Choose the level.</Form.Control.Feedback>
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Degree title <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="text"
                  required
                  maxLength={150}
                  placeholder="M.Sc, M.A, B.Ed …"
                  value={entry.Degree_Title}
                  onChange={(e) => set("Degree_Title", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">Enter the degree title.</Form.Control.Feedback>
              </div>
              <div className="col-md-4">
                <label className="form-label">Major subject</label>
                <Form.Control
                  type="text"
                  maxLength={150}
                  placeholder="Chemistry, English …"
                  value={entry.Major_Subject}
                  onChange={(e) => set("Major_Subject", e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  University / Board / Institution <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="text"
                  required
                  maxLength={250}
                  value={entry.Institution}
                  onChange={(e) => set("Institution", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">Enter the institution.</Form.Control.Feedback>
              </div>
              <div className="col-md-3">
                <label className="form-label">Study mode</label>
                <Form.Select value={entry.Study_Mode} onChange={(e) => set("Study_Mode", e.target.value)}>
                  <option value="Regular">Regular</option>
                  <option value="Private">Private</option>
                  <option value="Distance">Distance learning</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Passing year</label>
                <Form.Control
                  type="number"
                  min={1950}
                  max={new Date().getFullYear() + 1}
                  step={1}
                  placeholder={String(new Date().getFullYear())}
                  value={entry.Passing_Year}
                  onChange={(e) => set("Passing_Year", e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Total marks</label>
                <Form.Control
                  type="number"
                  min={1}
                  step="0.01"
                  value={entry.Total_Marks}
                  onChange={(e) => set("Total_Marks", e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Obtained marks</label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  isInvalid={marksExceed}
                  value={entry.Obtained_Marks}
                  onChange={(e) => set("Obtained_Marks", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">Obtained marks exceed total marks.</Form.Control.Feedback>
              </div>
              <div className="col-md-2">
                <label className="form-label">Percentage</label>
                <div className="form-control bg-light" style={{ minHeight: "38px" }}>
                  {marksExceed ? (
                    <span className="text-danger">—</span>
                  ) : livePercentage !== null ? (
                    `${livePercentage}%`
                  ) : (
                    <span className="text-muted">auto</span>
                  )}
                </div>
              </div>
              <div className="col-md-2">
                <label className="form-label">CGPA</label>
                <Form.Control
                  type="number"
                  min={0}
                  max={5}
                  step="0.01"
                  placeholder="0–5"
                  value={entry.CGPA}
                  onChange={(e) => set("CGPA", e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Grade</label>
                <Form.Control
                  type="text"
                  maxLength={20}
                  placeholder="A, B+ …"
                  value={entry.Grade}
                  onChange={(e) => set("Grade", e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Scanned copy of degree</label>
                <Form.Control type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onFileChosen} />
                <div className="form-text">PDF, JPG or PNG, up to 5 MB.</div>
                {file ? (
                  <div className="small mt-1">
                    <i className="ri-attachment-2"></i> {file.name} ({sizeLabel(file.size)}) — will replace the
                    current copy on save
                  </div>
                ) : entry.Attachment_Url ? (
                  <div className="small mt-1">
                    <i className="ri-attachment-2"></i>{" "}
                    <a href={config.root + entry.Attachment_Url} target="_blank" rel="noreferrer">
                      {entry.Attachment_Original_Name}
                    </a>{" "}
                    on file
                    <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-danger" onClick={removeAttachment}>
                      remove
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="col-md-6">
                <label className="form-label">Remarks</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={500}
                  value={entry.Remarks}
                  onChange={(e) => set("Remarks", e.target.value)}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="button button-light" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Saving…" : entry.ID ? "Update Qualification" : "Add Qualification"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* verification */}
      <Modal className="modal-theme" show={showVerify} onHide={() => setShowVerify(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px" }}>Degree verification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {verifyRow ? (
            <>
              <p className="mb-2">
                <b>{verifyRow.Degree_Title}</b>
                {verifyRow.Major_Subject ? ` (${verifyRow.Major_Subject})` : ""} — {verifyRow.Institution}
                {verifyRow.Passing_Year ? `, ${verifyRow.Passing_Year}` : ""}
              </p>
              <p className="small text-muted">
                {verifyRow.Is_Verified
                  ? `Currently verified by ${verifyRow.Verified_By || "—"} on ${
                      verifyRow.Verified_Date ? moment(verifyRow.Verified_Date).format("DD-MMM-YYYY") : "—"
                    }.`
                  : "Not yet verified with the issuing university or board."}
              </p>
              <label className="form-label">Verification remarks</label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={500}
                placeholder="e.g. HEC attestation seen, ref. no. …"
                value={verifyRemarks}
                onChange={(e) => setVerifyRemarks(e.target.value)}
              />
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="button button-light" onClick={() => setShowVerify(false)}>
            Cancel
          </button>
          {verifyRow && verifyRow.Is_Verified ? (
            <button type="button" className="button button-outline-primary" onClick={() => setVerification(false)}>
              Clear Verification
            </button>
          ) : null}
          <button type="button" className="button button-primary" onClick={() => setVerification(true)}>
            Mark as Verified
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default StaffQualifications;
